import { type FC, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import {
  useGetUserQuarriesForClaimQuery,
  useUpgradeQuarryMutation,
} from '@api/queries/quarry';
import { UpgradeBuild } from '@common/components/UpgradeBuild/UpgradeBuild';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { updateStorageAndBalance } from '@redux/reducers/userReducer';
import { createRequirementsList } from '@redux/selectors/lib';
import { selectQuarryByIdOrName } from '@redux/selectors/quarrySelectors';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { completeNewLevelStep } from '@/common/lib/completeNewLevelStep';
import { firebaseLogUpgradeBuilding } from '@/common/lib/firebaseLog';
import { SendAnalitics } from '@/common/lib/playdeck';
import { selectNextStepPlant } from '@/redux/selectors/userSelectors';

import { ConstructSucces } from '../../../common/components/ConstructSuccess/ConstructSuccess';

const QuarryUpgrade: FC = () => {
  const [skip, setSkip] = useState(false);
  useGetUserQuarriesForClaimQuery(undefined, {
    skip,
    refetchOnMountOrArgChange: true,
  });
  const { id } = useParams();
  const [isConstructComplete, setIsConstructComplete] = useState(false);
  const quarry = useAppSelector((state) => selectQuarryByIdOrName(state, id));
  const currentStep = useAppSelector(selectNextStepPlant);
  const [upgradeQuarry, { isLoading }] = useUpgradeQuarryMutation();
  const requirementsList = useMemo(
    () => createRequirementsList(quarry?.nextLevelRequirements),
    [quarry?.nextLevelRequirements],
  );
  const { isComplete } = useCheckRequirements(quarry?.nextLevelRequirements);
  const dispatch = useAppDispatch();
  const onQuarryUpgrade = useCallback((): void => {
    if (!quarry) return;
    const { id: item_id, name: item, level: item_level } = quarry;

    setSkip(true);
    upgradeQuarry({ id: item_id }).then(({ data }) => {
      SendAnalitics.buildDone({
        type: 'resources',
        item_id,
        item,
        item_level,
      });
      setIsConstructComplete(true);
      dispatch(updateStorageAndBalance(requirementsList));
      completeNewLevelStep({
        data: { name: data?.name, level: data?.level },
        dispatch,
        currentStep: {
          id: currentStep?.id,
          name: currentStep?.name,
          level: currentStep?.level,
        },
      });
    });

    if (quarry && quarry.nextLevelRequirements)
      firebaseLogUpgradeBuilding(
        {
          name: quarry.name,
          id: quarry.id,
          level: quarry.level + 1,
        },
        quarry.nextLevelRequirements,
      );
  }, [quarry?.id, upgradeQuarry, requirementsList, dispatch]);

  if (!quarry || !requirementsList) {
    return null;
  }

  return isConstructComplete ? (
    <ConstructSucces
      name={quarry.name}
      kind="quarry"
      id={quarry.id}
      level={quarry.level}
    />
  ) : (
    <>
      <Helmet>
        <title>Quarry_Upgrade</title>
      </Helmet>
      <UpgradeBuild
        name={quarry.name}
        id={quarry.id}
        level={quarry.level}
        kind="quarry"
        isComplete={isComplete}
        onUpgrade={onQuarryUpgrade}
        isLoading={isLoading}
        requirementsList={requirementsList}
      />
    </>
  );
};

export { QuarryUpgrade };
